'use client'

import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from '@/shared/navigation'
import {
  fetchKitchenTransferTrips,
  fetchKitchenTransferTrip,
  generateKitchenTransferQr,
  isKitchenTransferQrExpired,
  formatTransferLineSummary,
  formatTransferLineLabel,
  type ApiKitchenTransferDrop,
  type ApiKitchenTransferTrip,
  type ApiKitchenTransferLine,
} from '@/features/delman/api/delmanKitchenTransferApi'
import { delmanTerms } from '@/features/delman/delmanTerms'
import { DelmanPageHeader } from '@/features/delman/components/DelmanPageHeader'
import { EmptyState } from '@/shared/components/EmptyState'
import { SkeletonList } from '@/shared/components/Skeleton'
import { getDelmanDevice } from '@/shared/auth/delmanDeviceStorage'
import { QrDisplay } from '@/shared/components/QrDisplay'
import { Toast } from '@/shared/components/Toast'
import { todayDateKey } from '@/features/kitchen/storage/attendanceStorage'
import { useClientHydrated } from '@/shared/hooks/useClientHydrated'
import { qrCountdownLabel } from '@/shared/utils/qrCountdown'
import { buildKitchenTransferHandoverQrPayload } from '@/shared/utils/qrHandoverPayload'

function dropStatusBadge(status: string): string {
  if (status === 'receiving_completed') return 'badge-success'
  if (status === 'receiving_draft' || status === 'qr_shown') return 'badge-warning'
  return 'badge-draft'
}

function dropStatusLabel(status: string): string {
  if (status === 'receiving_completed') return delmanTerms.kitchenTransfer.dropDone
  if (status === 'receiving_draft') return delmanTerms.kitchenTransfer.dropScanned
  return delmanTerms.kitchenTransfer.dropPending
}

export function DelmanKitchenTransferListPage() {
  const hydrated = useClientHydrated()
  const deviceId = hydrated ? getDelmanDevice()?.deviceId ?? null : null
  const [trips, setTrips] = useState<ApiKitchenTransferTrip[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)

  useEffect(() => {
    if (!deviceId) {
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)
    setLoadError(null)

    void fetchKitchenTransferTrips(todayDateKey())
      .then((items) => {
        if (!cancelled) setTrips(items)
      })
      .catch(() => {
        if (!cancelled) setLoadError(delmanTerms.kitchenTransfer.loadError)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [deviceId, refreshKey])

  return (
    <section className="space-y-4">
      <div className="card relative space-y-2">
        <button
          type="button"
          className="btn-outline absolute top-3 right-3 min-w-[44px] px-3 z-10"
          onClick={() => setRefreshKey((k) => k + 1)}
          disabled={loading}
          aria-label={delmanTerms.kitchenTransfer.refreshAria}
          title={delmanTerms.kitchenTransfer.refreshAria}
        >
          <span className={`material-symbols-outlined ${loading ? 'animate-spin' : ''}`}>refresh</span>
        </button>
        <div className="pr-12">
          <p className="text-headline-md text-primary break-words">{delmanTerms.kitchenTransfer.pageTitle}</p>
          <p className="text-body-sm text-on-surface-variant mt-1">{delmanTerms.kitchenTransfer.pageSubtitle}</p>
        </div>
        <p className="text-body-sm text-on-surface-variant bg-surface-container rounded-lg px-3 py-2 border border-outline-variant">
          {delmanTerms.kitchenTransfer.whatIsTransfer}
        </p>
      </div>

      {loadError && (
        <div role="alert" className="alert-error flex flex-wrap items-center justify-between gap-2">
          <span className="text-body-sm">{loadError}</span>
          <button
            type="button"
            className="btn-outline min-h-[36px] text-body-sm"
            onClick={() => setRefreshKey((k) => k + 1)}
          >
            {delmanTerms.kitchenTransfer.retry}
          </button>
        </div>
      )}

      {loading && <SkeletonList count={2} label={delmanTerms.kitchenTransfer.loading} />}

      {!loading && !loadError && trips.length === 0 && (
        <EmptyState
          icon="swap_horiz"
          title={delmanTerms.kitchenTransfer.emptyTitle}
          description={delmanTerms.kitchenTransfer.empty}
        />
      )}

      {!loading &&
        trips.map((trip) => (
          <div key={trip.id} className="card space-y-3">
            <div>
              <p className="text-body-sm font-mono text-on-surface-variant">{trip.trip_code}</p>
              <p className="text-body-lg font-semibold text-on-surface mt-1 break-words">
                {delmanTerms.kitchenTransfer.fromKitchen(trip.source_kitchen_name)}
              </p>
            </div>
            <div className="space-y-2">
              {trip.transfers.map((drop) => (
                <Link
                  key={drop.id}
                  href={`/delman/kitchen-transfer/${drop.id}?tripId=${trip.id}`}
                  className="block rounded-lg border border-outline-variant p-3 hover:border-primary transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-body-sm font-semibold text-on-surface break-words">
                        {delmanTerms.kitchenTransfer.toKitchen(drop.target_kitchen_name)}
                      </p>
                      <p className="text-body-sm text-on-surface-variant mt-1">
                        {formatTransferLineSummary(drop.lines)}
                      </p>
                    </div>
                    <span className={`shrink-0 ${dropStatusBadge(drop.status)}`}>
                      {dropStatusLabel(drop.status)}
                    </span>
                  </div>
                  <p className="text-body-sm text-primary font-semibold mt-2">
                    {drop.status === 'receiving_completed'
                      ? delmanTerms.kitchenTransfer.dropDone
                      : delmanTerms.kitchenTransfer.dropAction}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        ))}
    </section>
  )
}

export function DelmanKitchenTransferHandoverPage() {
  const { transferId: transferIdParam } = useParams<{ transferId: string }>()
  const transferId = transferIdParam ?? ''
  const hydrated = useClientHydrated()
  const device = hydrated ? getDelmanDevice() : null
  const [drop, setDrop] = useState<ApiKitchenTransferDrop | null>(null)
  const [token, setToken] = useState('')
  const [expiresAt, setExpiresAt] = useState('')
  const [loading, setLoading] = useState(false)
  const [dropLoading, setDropLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const [toast, setToast] = useState<string | null>(null)
  const [now, setNow] = useState(0)

  useEffect(() => {
    setNow(Date.now())
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [])

  const loadDrop = useCallback(async () => {
    if (!transferId || typeof window === 'undefined') {
      setDropLoading(false)
      return
    }
    const tripId = new URLSearchParams(window.location.search).get('tripId')
    if (!tripId) {
      setDropLoading(false)
      setLoadError(delmanTerms.kitchenTransfer.dropNotFound)
      return
    }
    setLoadError(null)
    try {
      const trip = await fetchKitchenTransferTrip(tripId)
      const found = trip.transfers.find((t) => t.id === transferId) ?? null
      setDrop(found)
      if (!found) {
        setLoadError(delmanTerms.kitchenTransfer.dropNotFound)
      }
      if (found?.handover_qr_token && found.handover_qr_expires_at) {
        setToken(found.handover_qr_token)
        setExpiresAt(found.handover_qr_expires_at)
      } else {
        setToken('')
        setExpiresAt('')
      }
    } catch {
      setLoadError(delmanTerms.kitchenTransfer.loadError)
    } finally {
      setDropLoading(false)
    }
  }, [transferId])

  useEffect(() => {
    setDropLoading(true)
    void loadDrop()
  }, [loadDrop, refreshKey])

  useEffect(() => {
    if (!drop || drop.status === 'receiving_completed') return undefined
    const timer = window.setInterval(() => void loadDrop(), 3_000)
    return () => window.clearInterval(timer)
  }, [drop?.status, loadDrop])

  useEffect(() => {
    if (!drop || drop.status === 'qr_shown' || drop.status === 'pending') return
    setToken('')
    setExpiresAt('')
  }, [drop?.status])

  async function showQr(force = false) {
    if (!device || !transferId) return
    setLoading(true)
    try {
      const res = await generateKitchenTransferQr(transferId, device.deviceId, { force })
      setToken(res.token)
      setExpiresAt(res.expires_at)
      await loadDrop()
    } catch (e) {
      setToast(e instanceof Error ? e.message : delmanTerms.kitchenTransfer.qrCreateError)
    } finally {
      setLoading(false)
    }
  }

  const isReceived = drop?.status === 'receiving_completed'
  const isScanned = drop?.status === 'receiving_draft'
  const canShowQr = !isReceived && !isScanned
  const expired = isKitchenTransferQrExpired(expiresAt)
  const ttlLabel = qrCountdownLabel(
    expiresAt,
    now,
    delmanTerms.handover.ttlExpired,
    delmanTerms.handover.ttlRemaining,
  )
  const lineSummary = drop ? formatTransferLineSummary(drop.lines) : ''
  const qrPayload =
    token && transferId ? buildKitchenTransferHandoverQrPayload(transferId, token) : ''

  if (!hydrated) {
    return <div className="min-h-[200px]" aria-hidden />
  }

  if (!transferId) {
    return (
      <section className="card space-y-3">
        <p className="text-body-sm text-status-danger">{delmanTerms.kitchenTransfer.dropNotFound}</p>
        <Link href="/delman/kitchen-transfer" className="btn-outline block text-center">
          {delmanTerms.kitchenTransfer.backToList}
        </Link>
      </section>
    )
  }

  if (dropLoading && !drop) {
    return (
      <section className="space-y-3">
        <SkeletonList count={2} label={delmanTerms.kitchenTransfer.dropLoading} />
      </section>
    )
  }

  if (!drop && loadError) {
    return (
      <section className="space-y-3">
        <div role="alert" className="alert-error flex flex-wrap items-center justify-between gap-2">
          <span className="text-body-sm">{loadError}</span>
          <button
            type="button"
            className="btn-outline min-h-[36px] text-body-sm"
            onClick={() => setRefreshKey((k) => k + 1)}
          >
            {delmanTerms.kitchenTransfer.retry}
          </button>
        </div>
        <Link href="/delman/kitchen-transfer" className="btn-outline block text-center">
          {delmanTerms.kitchenTransfer.backToList}
        </Link>
      </section>
    )
  }

  return (
    <section className="space-y-4">
      <div className="relative">
        <button
          type="button"
          className="btn-outline absolute top-0 right-0 min-w-[44px] px-3 z-10"
          onClick={() => setRefreshKey((k) => k + 1)}
          disabled={dropLoading}
          aria-label={delmanTerms.kitchenTransfer.refreshDropAria}
          title={delmanTerms.kitchenTransfer.refreshDropAria}
        >
          <span className={`material-symbols-outlined ${dropLoading ? 'animate-spin' : ''}`}>refresh</span>
        </button>
        <div className="pr-12">
          <DelmanPageHeader
            title={delmanTerms.kitchenTransfer.handoverPageTitle}
            backHref="/delman/kitchen-transfer"
            backLabel={delmanTerms.kitchenTransfer.backToList}
          />
        </div>
      </div>

      {loadError && drop && (
        <div role="alert" className="alert-error flex flex-wrap items-center justify-between gap-2">
          <span className="text-body-sm">{loadError}</span>
          <button
            type="button"
            className="btn-outline min-h-[36px] text-body-sm"
            onClick={() => setRefreshKey((k) => k + 1)}
          >
            {delmanTerms.kitchenTransfer.retry}
          </button>
        </div>
      )}

      {drop && (
        <div className="card space-y-2">
          <p className="text-headline-md text-primary break-words">
            {delmanTerms.kitchenTransfer.toKitchen(drop.target_kitchen_name)}
          </p>
          <p className="text-body-sm text-on-surface-variant break-words">
            {delmanTerms.kitchenTransfer.fromKitchen(drop.source_kitchen_name)} · {lineSummary}
          </p>
          <ul className="text-body-sm text-on-surface-variant space-y-1 border-t border-surface-variant pt-2">
            {drop.lines.map((line: ApiKitchenTransferLine, i: number) => (
              <li key={`${line.item_type}-${line.product_name ?? line.material_name}-${i}`}>
                {formatTransferLineLabel(line)}
              </li>
            ))}
          </ul>
        </div>
      )}
      {isReceived && (
        <div className="card bg-primary-container/30 border border-primary/20">
          <p className="text-body-sm font-semibold text-primary">{delmanTerms.kitchenTransfer.handoverDone}</p>
        </div>
      )}
      {isScanned && (
        <div className="card bg-warning-container/30 border border-warning/20">
          <p className="text-body-sm font-semibold text-on-surface">{delmanTerms.kitchenTransfer.handoverScanned}</p>
        </div>
      )}
      {canShowQr && token ? (
        <div className="card flex flex-col items-center gap-3">
          <QrDisplay token={qrPayload} size="large" />
          <p className={`text-body-sm ${expired ? 'text-error' : 'text-on-surface-variant'}`}>
            {expired ? delmanTerms.kitchenTransfer.qrExpired : ttlLabel}
          </p>
          <p className="text-body-sm font-mono text-on-surface-variant break-all text-center">{qrPayload}</p>
        </div>
      ) : canShowQr ? (
        <p className="text-body-sm text-on-surface-variant">{delmanTerms.kitchenTransfer.handoverHint}</p>
      ) : null}
      {canShowQr && (!token || expired) && (
        <button
          type="button"
          className="btn-primary w-full min-h-[44px]"
          disabled={loading}
          onClick={() => void showQr(expired)}
        >
          {!token ? delmanTerms.kitchenTransfer.showQr : delmanTerms.kitchenTransfer.refreshQr}
        </button>
      )}
      {toast && <Toast message={toast} tone="info" onDismiss={() => setToast(null)} />}
    </section>
  )
}
